import { ButtonPrimary } from "@nova/component-btn"
import { HighPriorityNovaModalContainer } from "@nova/component-nova-modal-container"
import { ScrollableArea } from "@nova/component-scrollable-area"
import { ID, MemberType, Permissions } from "@nova/constants"
import { useTranslation } from "@nova/use-translation"
import {
    selectActiveWorkspace,
    selectActiveWorkspaceId,
    selectTags,
} from "features/data-management/workspace/WorkspaceManagementSlice"
import { selectAllFires } from "features/entity-caching/fires/selectors"
import {
    selectActiveMember,
    setActiveMember,
    updateManyMemberPermissionsHandler,
} from "features/entity-caching/members/UserManagementSlice"
import { findTagAncestors } from "features/data-management/workspace/module"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { get } from "nodash"
import { useCallback, useEffect, useMemo, useState } from "react"
import { AccessRequestButtons } from "../Invite/AccessRequestButtons"
import { WorkspaceAccessSplitButton } from "features/data-management/workspace/workspace/body/settings-and-collaborators/collaborators/WorkspaceAccessSplitButton"
import { OrganizationPermissions, WorkspaceMember } from "@nova/schemas"
import { hideGlobalModal } from "runlevel/actions"

const FRONT_ELLIPSIS = "..."
const MAX_TITLE_LENGTH = 65

const truncateTitleFromFront = (value: string, maxLength = MAX_TITLE_LENGTH) => {
    const trimmed = (value ?? "").trim()
    if (!trimmed) return ""

    if (trimmed.length <= maxLength) return trimmed

    const textLength = Math.max(0, maxLength - FRONT_ELLIPSIS.length)

    const result = trimmed.slice(trimmed.length - textLength)

    return `${FRONT_ELLIPSIS}${result}`
}

export const WorkspaceAccessModal = () => {
    const dispatch = useAppDispatch()

    const activeMember = useAppSelector(selectActiveMember())
    const activeWorkspaceId = useAppSelector(selectActiveWorkspaceId()) || ""
    const [permissionUpdates, setPermissionUpdates] = useState(
        activeMember?.permissions || ({} as OrganizationPermissions)
    )

    const handleCloseAccessModal = useCallback(() => {
        dispatch(hideGlobalModal())
        dispatch(setActiveMember(undefined))
    }, [dispatch])

    useEffect(() => {
        setPermissionUpdates(activeMember?.permissions || ({} as OrganizationPermissions))
    }, [activeMember?.permissions])

    const handlePermissionUpdate = useCallback(() => {
        if (!activeMember || !activeWorkspaceId || !permissionUpdates) return

        dispatch(
            updateManyMemberPermissionsHandler({
                workspaceId: activeWorkspaceId,
                member: activeMember,
                newPermissions: permissionUpdates,
            })
        )
    }, [activeWorkspaceId, permissionUpdates, activeMember, dispatch])

    if (!activeMember) return null

    const permissions: { permission: Permissions; entityId: string }[] = []
    if (activeMember.permissions.workspaceAccess)
        permissions.push({
            permission: activeMember.permissions.workspaceAccess,
            entityId: activeWorkspaceId,
        })

    activeMember.permissions.subRoles?.forEach((role) => {
        permissions.push({ entityId: role.id, permission: role.permission })
    })
    activeMember.permissions.accessRequestIds?.forEach((id) => {
        if (permissions.find((p) => p.entityId === id)) return
        permissions.push({
            entityId: id,
            permission: Permissions.Viewer,
        })
    })
    const sortedPermission = [...permissions].sort((a, b) => {
        const aVal = (a as Record<string, unknown>)["title"]
        const bVal = (b as Record<string, unknown>)["title"]
        if (aVal == null && bVal == null) return 0
        if (aVal == null) return 1
        if (bVal == null) return -1
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0
    })
    return (
        <>
            <HighPriorityNovaModalContainer
                title="Workspace Access"
                className="w-[30rem]"
                bottomBar={
                    <div className="w-full flex justify-end items-center gap-2">
                        <ButtonPrimary
                            onClick={() => {
                                handlePermissionUpdate()
                                handleCloseAccessModal()
                            }}
                            title="Confirm Access"
                        />
                    </div>
                }
                closeModal={handleCloseAccessModal}
            >
                <ScrollableArea className="max-h-[320px] flex flex-col gap-3 p-4 mt-6 border border-gray-700 rounded-lg">
                    {sortedPermission.map((permission, idx) => {
                        return (
                            <WorkspaceAccessModalRow
                                key={idx}
                                member={activeMember}
                                permission={permission.permission}
                                entityId={permission.entityId}
                                setPermissionUpdates={setPermissionUpdates}
                                handleCloseAccessModal={handleCloseAccessModal}
                            />
                        )
                    })}
                </ScrollableArea>
            </HighPriorityNovaModalContainer>
            <div id="access-modal-portal" />
        </>
    )
}

const WorkspaceAccessModalRow = ({
    member,
    permission,
    entityId,
    setPermissionUpdates,
    handleCloseAccessModal,
}: {
    member: WorkspaceMember
    permission: Permissions
    entityId: ID
    setPermissionUpdates: React.Dispatch<React.SetStateAction<OrganizationPermissions>>
    handleCloseAccessModal: () => void
}) => {
    const { t } = useTranslation()

    const projects = useAppSelector(selectAllFires)
    const tags = useAppSelector(selectTags)

    const workspaceId = useAppSelector(selectActiveWorkspaceId())
    const isUpdatingWorkspacePermissions = entityId === workspaceId

    const activeWorkspace = useAppSelector(selectActiveWorkspace())!
    const workspaceName = activeWorkspace?.name || "Workspace"

    const [dropdownSelection, setDropdownSelection] = useState(permission || Permissions.None)
    const hasRequestedAccess = member.permissions.accessRequestIds?.includes(entityId)

    useEffect(() => {
        setDropdownSelection(permission)
    }, [permission])

    const handlePermissionSelection = useCallback(
        (selection: Permissions) => {
            setDropdownSelection(selection)

            setPermissionUpdates((prevUpdates) => {
                const filteredSubs =
                    prevUpdates.subRoles?.filter((prev) => prev.id !== entityId) || []
                const newSubRoles =
                    selection !== Permissions.None && !isUpdatingWorkspacePermissions
                        ? [...filteredSubs, { id: entityId, permission: selection }]
                        : filteredSubs
                return {
                    ...prevUpdates,
                    workspaceAccess: isUpdatingWorkspacePermissions
                        ? selection
                        : prevUpdates.workspaceAccess,
                    subRoles: newSubRoles,
                }
            })
        },
        [entityId, isUpdatingWorkspacePermissions, setPermissionUpdates]
    )

    const ancestorTargetId = projects[entityId]?.tagId ?? entityId

    const ancestors = useMemo(
        () =>
            activeWorkspace.tags.length && ancestorTargetId
                ? findTagAncestors(activeWorkspace.tags, ancestorTargetId)
                : [],
        [ancestorTargetId, activeWorkspace.tags]
    )

    const getProjectPath = () => {
        const projectTitle =
            get(projects[entityId], "name") ||
            tags.find((tag) => tag.id === entityId)?.name ||
            "(untitled)"

        const folders = ancestors.slice(1)

        const visibleFolders: string[] = []

        for (let i = folders.length - 1; i >= 0; i--) {
            const segment = folders[i].name ?? ""
            if (!segment) continue
            visibleFolders.unshift(segment)
        }

        const parts = [workspaceName, ...visibleFolders, projectTitle].filter(Boolean)
        return parts.join(" / ")
    }

    const title = isUpdatingWorkspacePermissions ? "Workspace" : getProjectPath()
    const displayTitle = t(truncateTitleFromFront(title || "Untitled"))

    return (
        <div className="flex items-center justify-between gap-3">
            <h5
                className="text-gray-100"
                style={{
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                }}
            >
                {displayTitle}
            </h5>
            {!hasRequestedAccess && (
                <WorkspaceAccessSplitButton
                    defaultPermission={permission}
                    dropdownSelection={dropdownSelection}
                    accessEntityId={entityId}
                    setDropdownSelection={handlePermissionSelection}
                    portalPositioning={{
                        portalId: "access-modal-portal",
                        isFixedPositioned: true,
                        relativeOffset: 38,
                    }}
                />
            )}
            {hasRequestedAccess && member.type === MemberType.Member && (
                <AccessRequestButtons
                    entityId={entityId}
                    member={member}
                    onClose={handleCloseAccessModal}
                />
            )}
        </div>
    )
}
